import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { createNotification, getNotifications } from '@/lib/api/notification';
import { useEffect } from 'react';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { formatDistance } from 'date-fns';

const FormSchema = z.object({
  cancelSnooze: z.boolean().default(false),
  snoozeRequest: z.boolean().default(false),
  disabled: z.boolean(),
});

function submitNotification(formValues: z.infer<typeof FormSchema>) {
  return createNotification({
    disabled: formValues.disabled,
    snooze: formValues.snoozeRequest,
  });
}

export function NotificationForm() {
  const queryClient = useQueryClient();

  const {
    data: notificationConfig,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['NOTIFICATION'],
    queryFn: getNotifications,
  });

  const mutation = useMutation({
    mutationFn: submitNotification,
    onSuccess: (value) => {
      queryClient.setQueryData(['NOTIFICATION'], () => value);
    },
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      disabled: false,
    },
    values: {
      disabled: notificationConfig?.disabled,
      snoozeRequest: false,
      cancelSnooze:
        !notificationConfig?.snoozeUntil ||
        Date.now() > notificationConfig.snoozeUntil,
    },
  });

  const field1Value = form.watch('disabled');
  useEffect(() => {
    if (field1Value) {
      form.setValue('snoozeRequest', false);
    }
  }, [field1Value, form.setValue]);

  if (isLoading || isFetching) {
    return;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Notification</h3>
        <p className="text-sm text-muted-foreground">
          Configure how you receive notifications.
        </p>
      </div>
      <Separator />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((value) => mutation.mutateAsync(value))}
          className="w-full space-y-6"
        >
          <div>
            <div className="space-y-4">
              {!form.getValues().cancelSnooze && (
                <FormField
                  control={form.control}
                  name="cancelSnooze"
                  render={({ field }) => (
                    <FormItem className="flex bg-violet-700 text-white flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Temporary disable notification
                        </FormLabel>
                        <FormDescription className="text-white">
                          {notificationConfig?.snoozeUntil
                            ? formatDistance(
                                notificationConfig.snoozeUntil,
                                new Date(),
                                {
                                  addSuffix: true,
                                }
                              )
                            : '--'}
                        </FormDescription>
                      </div>
                      <Button
                        className="mt-0"
                        type="button"
                        onClick={() => field.onChange(true)}
                      >
                        Cancel
                      </Button>
                    </FormItem>
                  )}
                />
              )}
              {form.getValues().cancelSnooze && (
                <FormField
                  control={form.control}
                  name="snoozeRequest"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Temporary disable notification
                        </FormLabel>
                        <FormDescription>
                          Snooze notification for the next 10 minutes.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          disabled={form.getValues().disabled}
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="disabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Permanently disable notification
                      </FormLabel>
                      <FormDescription>
                        Notification will be pause until they get reactivated.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={form.formState.isSubmitting || !form.formState.isDirty}
          >
            Update Notification Preference
          </Button>
        </form>
      </Form>
    </div>
  );
}
